import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { EyeIcon, EyeSlashIcon } from '@heroicons/react/24/outline';
import { useAuth } from '../contexts/authContextValue';
import { useTranslation } from 'react-i18next';
import { LoginCredentials } from '../types';
import { getErrorMessage } from '../utils/errors';

const Login: React.FC = () => {
  const [credentials, setCredentials] = useState<LoginCredentials>({
    email: '',
    password: '',
  });
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  const { login } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      await login(credentials);
      console.log('✅ Login completed successfully');
      setIsLoading(false);
      
      // Wait a bit to ensure state is updated
      await new Promise(resolve => setTimeout(resolve, 50));
      
      // Navigation zum Dashboard
      console.log('🔄 Navigating to dashboard...');
      navigate('/', { replace: true });
    } catch (err: unknown) {
      console.error('Login error:', err);
      setIsLoading(false);
      setError(getErrorMessage(err, t('auth.loginError')));
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setCredentials(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  return (
    <div className="login-page min-h-screen flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full">
        <div className="card">
          <div className="login-head">
            <div className="mx-auto login-logo flex items-center justify-center">
              <img
                src="/images/EE_Signet_Gradient.png"
                alt="Energetic Eternity Logo"
                className="w-full h-full object-contain"
              />
            </div>
            <h2 className="login-title text-3xl font-bold">
              {t('auth.loginTitle')}
            </h2>
            <p className="login-sub text-sm">
              {t('auth.loginSubtitle')}
            </p>
          </div>

          <div className="card-content">
            <form onSubmit={handleSubmit} className="space-y-6">
              {error && (
                <div className="bg-error-50 border border-error-200 rounded-md p-4">
                  <p className="text-sm text-error-600">{error}</p>
                </div>
              )}

              <div>
                <label htmlFor="email" className="block text-sm font-medium text-secondary-700 mb-2">
                  {t('auth.email')}
                </label>
                <input
                  id="email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  required
                  value={credentials.email}
                  onChange={handleChange}
                  className="input"
                  placeholder={t('auth.email')}
                />
              </div>

              <div>
                <label htmlFor="password" className="block text-sm font-medium text-secondary-700 mb-2">
                  {t('auth.password')}
                </label>
                <div className="relative">
                  <input
                    id="password"
                    name="password"
                    type={showPassword ? 'text' : 'password'}
                    autoComplete="current-password"
                    required
                    value={credentials.password}
                    onChange={handleChange}
                    className="input pr-10"
                    placeholder={t('auth.password')}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute inset-y-0 right-0 pr-3 flex items-center"
                  >
                    {showPassword ? (
                      <EyeSlashIcon className="w-4 h-4 text-secondary-400" />
                    ) : (
                      <EyeIcon className="w-4 h-4 text-secondary-400" />
                    )}
                  </button>
                </div>
              </div>

              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <input
                    id="remember-me"
                    name="remember-me"
                    type="checkbox"
                    className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-secondary-300 rounded"
                  />
                  <label htmlFor="remember-me" className="ml-2 block text-sm text-secondary-700">
                    {t('auth.rememberMe')}
                  </label>
                </div>

                <div className="text-sm">
                  <Link
                    to="/forgot-password"
                    className="font-medium text-primary-600 hover:text-primary-500"
                  >
                    {t('auth.forgotPassword')}
                  </Link>
                </div>
              </div>

              <button
                type="submit"
                disabled={isLoading}
                className="btn btn-primary btn-lg w-full"
              >
                {isLoading ? (
                  <div className="loading-spinner mr-2" />
                ) : null}
                {isLoading ? t('app.loading') : t('auth.loginButton')}
              </button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
